import React from 'react';
import { Grid, Switch, Typography } from '@mui/material';
import { currencyFormatter, formatDate } from 'helpers/formatHelper';
import { transformDateKeys } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/components/ValueByDate/helpers/apiHelpers';
import { firstNameLastNameFormatter } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/agentColumnHelpers';
import { DetailedAgentPayComponentHypothesis } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/PayComponent/helpers/types';
import { getPositionValue } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/gridHelpers';
import { humanizedRecurrentValues } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import { PayComponent } from 'routes/HumanResourcesModule/PayState/helpers/constants';
import { SimulationDateString } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { toggleExclusion } from '../helpers/dataHelpers';
import { useTranslation } from 'react-i18next';

type Props = {
    hypothesis: DetailedAgentPayComponentHypothesis;
    excludedAgentPayComponentHypotheses?: number[];
    setExcludedAgentPayComponentHypotheses?: (
        excludedAgentPayComponentHypotheses: number[],
    ) => void;
};

const AgentPayComponentHypothesisDisplay = ({
    hypothesis,
    excludedAgentPayComponentHypotheses = undefined,
    setExcludedAgentPayComponentHypotheses = undefined,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const {
        id,
        agentFirstName,
        agentLastName,
        payComponent,
        newValuesByDates,
        recurrence,
        mantyStatus,
    } = hypothesis;
    const valuesByDates = transformDateKeys(newValuesByDates);
    const isIfse = payComponent === PayComponent.IFSE_AND_MONTHLY_PRIMES;

    if (isIfse) {
        // an IFSE hypothesis should always have a value
        if (Object.entries(valuesByDates).length === 0) {
            throw new Error('No values found');
        }

        // all values should be the same for an IFSE hypothesis
        if (new Set(Object.values(valuesByDates)).size !== 1) {
            throw new Error('Not all values are the same');
        }
    }

    const sortedDates = Object.keys(valuesByDates).sort(
        (a, b) => new Date(a).getTime() - new Date(b).getTime(),
    );

    const sortedValuesByDates: Record<SimulationDateString, number> = {};
    sortedDates.forEach((key: SimulationDateString) => {
        sortedValuesByDates[key] = valuesByDates[key];
    });
    // Since all ifse values are the same, we can just take the first one to display

    const filteredValuesByDates = isIfse
        ? {
              [Object.keys(sortedValuesByDates)[0]]:
                  Object.values(sortedValuesByDates)[0],
          }
        : valuesByDates;

    const isExcluded =
        excludedAgentPayComponentHypotheses?.includes(id) ?? false;

    return (
        <>
            {Object.entries(filteredValuesByDates).map(([key, value]) => (
                <Grid container spacing={2} key={key}>
                    <Grid item xs={2}>
                        <Typography variant="body1SemiBold">
                            {firstNameLastNameFormatter({
                                lastName: agentLastName,
                                firstName: agentFirstName,
                            })}
                        </Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography
                            sx={{ textAlign: 'center' }}
                            variant="body1SemiBold"
                        >
                            {payComponent}
                        </Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography
                            sx={{ textAlign: 'center' }}
                            variant="body1SemiBold"
                        >
                            {humanizedRecurrentValues[recurrence]}
                        </Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography
                            sx={{ textAlign: 'center' }}
                            variant="body1SemiBold"
                        >
                            {getPositionValue(mantyStatus)}
                        </Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography
                            sx={{ textAlign: 'center' }}
                            variant="body2"
                        >
                            {isIfse
                                ? t(
                                      `humanResources.simulation.freeHypothesis.ifse.applicationDate`,
                                      { date: formatDate(new Date(key)) },
                                  )
                                : formatDate(new Date(key))}
                        </Typography>
                    </Grid>
                    <Grid item xs={1}>
                        <Typography
                            sx={{
                                textAlign: 'right',
                                marginRight: '20px',
                            }}
                            variant="body2"
                        >
                            {currencyFormatter(value)}
                        </Typography>
                    </Grid>
                    {excludedAgentPayComponentHypotheses &&
                        setExcludedAgentPayComponentHypotheses && (
                            <Grid
                                item
                                xs={1}
                                display="flex"
                                justifyContent="end"
                            >
                                <Switch
                                    checked={!isExcluded}
                                    onChange={() =>
                                        toggleExclusion(
                                            excludedAgentPayComponentHypotheses,
                                            setExcludedAgentPayComponentHypotheses,
                                            id,
                                            isExcluded,
                                        )
                                    }
                                />
                            </Grid>
                        )}
                </Grid>
            ))}
        </>
    );
};

export default AgentPayComponentHypothesisDisplay;
